/**
 * Presentational view for the per-user Memory tab (MEM-10, COP-032, D-20;
 * IMEM-20 / COP-105 permanent wipe). Split out of `./Memory` so the table
 * markup is renderable under the admin's `renderToStaticMarkup` test
 * convention: this file is props-only and MUST NOT import the HubClient
 * singleton (`@/lib/hub-client`) — that module constructs a HubClient at load
 * which throws in the test env (M12 list-view precedent). All data + handlers
 * arrive as props from `./Memory`.
 */
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Link } from 'react-router';
import type { MemoryRow } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { DataTable } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fmtIso, truncateId } from '@/lib/format';
import { cn } from '@/lib/utils';

export type ScopeFilter = 'all' | 'global' | 'track';

// Copy shared by both destructive actions when the caller cannot act on the
// target user's rows (D-20 — the DELETE routes key off request.user.id, so
// admin-initiated deletes of OTHER users' rows are deferred; RLS also makes
// any cross-user id a silent no-op).
export const SOFT_DELETE_DISABLED_TOOLTIP =
  "Admin-initiated soft-delete of other users' memory lands with the admin escape hatch in a future milestone.";

// IMEM-20 / COP-105: the permanence warning surfaced in the confirm dialog.
export const PERMANENT_DELETE_WARNING =
  'This cannot be undone — the memory is removed immediately and permanently.';

const truncate = (s: string, max: number): string =>
  s.length > max ? `${s.slice(0, max - 1)}…` : s;

const columns: ColumnDef<MemoryRow>[] = [
  {
    id: 'scope',
    header: 'Scope',
    accessorKey: 'scope',
    cell: ({ row }) => (
      <Badge variant={row.original.scope === 'global' ? 'default' : 'secondary'}>
        {row.original.scope}
      </Badge>
    ),
  },
  {
    id: 'content',
    header: 'Content',
    accessorKey: 'content',
    cell: ({ row }) => {
      const content = row.original.content;
      return (
        <span title={content} className="block max-w-md whitespace-pre-wrap text-sm">
          {truncate(content, 80)}
        </span>
      );
    },
  },
  {
    id: 'trackId',
    header: 'Track',
    accessorKey: 'trackId',
    cell: ({ row }) => {
      const trackId = row.original.trackId;
      if (!trackId) return <span className="text-muted-foreground">—</span>;
      return (
        <Link
          to={`/admin/tracks/show/${trackId}`}
          className="font-mono text-xs underline-offset-2 hover:underline"
          title={trackId}
        >
          {truncateId(trackId)}
        </Link>
      );
    },
  },
  {
    id: 'createdAt',
    header: 'Created',
    accessorKey: 'createdAt',
    cell: ({ row }) => (
      <span className="text-xs">{fmtIso(row.original.createdAt)}</span>
    ),
  },
  {
    id: 'deletedAt',
    header: 'Deleted',
    accessorKey: 'deletedAt',
    cell: ({ row }) =>
      row.original.deletedAt ? (
        <span className="text-xs">{fmtIso(row.original.deletedAt)}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

const scopeOptions: { value: ScopeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'global', label: 'Global' },
  { value: 'track', label: 'Track' },
];

export interface UserMemoryViewProps {
  scopeFilter: ScopeFilter;
  setScopeFilter: (value: ScopeFilter) => void;
  rows: MemoryRow[];
  isLoading: boolean;
  error: string | null;
  /** D-20 gate — false disables both delete actions with the shared tooltip. */
  canDelete: boolean;
  /** Row id whose soft-delete is in flight (drives the button's busy label). */
  pendingSoftDeleteId: string | null;
  onSoftDelete: (id: string) => void;
  refresh: () => void;
  /** Row whose permanent-wipe confirm dialog is open, or null when closed. */
  permanentDeleteTarget: MemoryRow | null;
  permanentDeleteBusy: boolean;
  onRequestPermanentDelete: (row: MemoryRow) => void;
  onConfirmPermanentDelete: () => void;
  onCancelPermanentDelete: () => void;
}

export function UserMemoryView({
  scopeFilter,
  setScopeFilter,
  rows,
  isLoading,
  error,
  canDelete,
  pendingSoftDeleteId,
  onSoftDelete,
  refresh,
  permanentDeleteTarget,
  permanentDeleteBusy,
  onRequestPermanentDelete,
  onConfirmPermanentDelete,
  onCancelPermanentDelete,
}: UserMemoryViewProps) {
  const table = useReactTable<MemoryRow>({
    data: rows,
    columns: [
      ...columns,
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const isDeleted = !!row.original.deletedAt;
          const isSoftPending = pendingSoftDeleteId === row.original.id;
          return (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                variant="destructive"
                aria-label="Soft-delete memory"
                disabled={!canDelete || isDeleted || isSoftPending}
                title={!canDelete ? SOFT_DELETE_DISABLED_TOOLTIP : undefined}
                onClick={() => onSoftDelete(row.original.id)}
              >
                {isSoftPending ? 'Working…' : 'Soft-delete'}
              </Button>
              {/*
                IMEM-20 / COP-105 permanent wipe — a DISTINCT destructive action
                from soft-delete above. Enabled even when the row is already
                soft-deleted (hardDelete works on active AND soft-deleted rows);
                gated only by canDelete (D-20). Clicking opens the confirm
                dialog below rather than acting immediately.
              */}
              <Button
                size="sm"
                variant="destructive"
                aria-label="Delete memory permanently"
                disabled={!canDelete}
                title={!canDelete ? SOFT_DELETE_DISABLED_TOOLTIP : undefined}
                onClick={() => onRequestPermanentDelete(row.original)}
              >
                Delete permanently
              </Button>
            </div>
          );
        },
      },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Scope</span>
          <div role="tablist" aria-label="Scope filter" className="inline-flex rounded-md border bg-background">
            {scopeOptions.map((opt) => {
              const isActive = scopeFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setScopeFilter(opt.value)}
                  className={cn(
                    'px-3 py-1 text-sm transition-colors first:rounded-l-md last:rounded-r-md',
                    'border-r last:border-r-0',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => refresh()}
          disabled={isLoading}
        >
          {isLoading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error ? <Alert>{error}</Alert> : null}

      <DataTable table={table} isLoading={isLoading} emptyMessage="No memory rows." />

      {!canDelete ? (
        <p className="text-xs text-muted-foreground">
          {SOFT_DELETE_DISABLED_TOOLTIP}
        </p>
      ) : null}

      <ConfirmDialog
        open={permanentDeleteTarget !== null}
        destructive
        title="Delete memory permanently"
        description={
          permanentDeleteTarget
            ? `Permanently delete the memory “${truncate(permanentDeleteTarget.content, 120)}”. ${PERMANENT_DELETE_WARNING}`
            : undefined
        }
        confirmLabel="Delete permanently"
        busy={permanentDeleteBusy}
        onConfirm={onConfirmPermanentDelete}
        onCancel={onCancelPermanentDelete}
      />
    </div>
  );
}
